import React, { useRef } from 'react';
import { AssetUrls } from 'config/assets';
import { FeedCardRef } from 'types/interfaces/followingFeed';
import './CardFan.css';

const CARD_BACK_DATA_URI =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

interface CardFanProps {
  cards: FeedCardRef[];
  max?: number;
  showOverflowLabel?: boolean;
  /** Used for TRADE_* where backend ships a preview slice but knows the full size. Defaults to cards.length. */
  totalCount?: number;
}

const ROTATION_CAP = 10;

// Max absolute rotation (deg) and translation (px, 120-card-width baseline) per
// fan count. Each card's concrete value is computed monotonically from -max
// (leftmost) to +max (rightmost) so the stack reads left-to-right with every
// card's top-left corner (card name) visible above the previous card.
// Translations are scaled by `--fan-scale` in CardFan.css (1.5 desktop, 0.75 mobile).
const FAN_BOUNDS: Record<number, { rot: number; trans: number }> = {
  1: { rot: 0, trans: 0 },
  2: { rot: 6, trans: 40 },
  3: { rot: 12, trans: 70 },
  4: { rot: 14, trans: 95 },
  5: { rot: 16, trans: 84 },
  6: { rot: 18, trans: 96 },
  7: { rot: 20, trans: 102 },
  8: { rot: 22, trans: 108 },
  9: { rot: 24, trans: 114 },
  10: { rot: 25, trans: 120 },
};

const monotonic = (i: number, shown: number, max: number): number =>
  shown === 1 ? 0 : -max + (i / (shown - 1)) * (2 * max);

export const CardFan: React.FC<CardFanProps> = ({
  cards,
  max = ROTATION_CAP,
  showOverflowLabel = false,
  totalCount,
}) => {
  const fallbackStepRef = useRef<Map<number, number>>(new Map());

  if (!cards || cards.length === 0) return null;

  const capped = Math.min(max, ROTATION_CAP);
  const displayCards = cards.slice(0, capped);
  const shown = displayCards.length;
  const total = totalCount ?? cards.length;
  const overflow = Math.max(0, total - shown);

  const bounds = FAN_BOUNDS[shown] ?? FAN_BOUNDS[1];

  const handleError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    card: FeedCardRef,
    index: number,
  ) => {
    const step = fallbackStepRef.current.get(index) ?? 0;
    fallbackStepRef.current.set(index, step + 1);

    const backendFallbacks = card.normal_fallbacks ?? [];
    const cdnFallbacks = card.scryfallId
      ? [
          AssetUrls.cards.getCardImage(card.scryfallId, 'normal'),
          AssetUrls.cards.getCardImage(card.scryfallId, 'small'),
          AssetUrls.cards.getCardImage(card.scryfallId, 'large'),
        ]
      : [];

    const initialUrl = card.imageUrl;
    const chain = [...backendFallbacks, ...cdnFallbacks].filter(
      (url) => url && url !== initialUrl,
    );

    if (step < chain.length) {
      e.currentTarget.src = chain[step];
      return;
    }

    e.currentTarget.src = CARD_BACK_DATA_URI;
  };

  return (
    <div className={`card-fan card-fan--count-${shown}`}>
      {displayCards.map((card, i) => {
        const rotation = monotonic(i, shown, bounds.rot);
        const translation = monotonic(i, shown, bounds.trans);
        return (
          <div
            key={`${card.cardId}-${i}`}
            className="card-fan__card"
            style={{
              transform: `rotate(${rotation}deg) translateX(calc(${translation}px * var(--fan-scale, 1)))`,
              zIndex: i,
            }}
          >
            <img
              src={card.imageUrl || CARD_BACK_DATA_URI}
              alt={card.cardName}
              loading="lazy"
              onError={(e) => handleError(e, card, i)}
            />
          </div>
        );
      })}

      {showOverflowLabel && overflow > 0 && (
        <span className="card-fan__overflow">+{overflow} more</span>
      )}
    </div>
  );
};

export default CardFan;
